<template>
    <div>
        <header>
            <v-container>
                <v-row no-gutters>
                    <h1 class="font-weight-light headline">
                        {{ $t("title.FAQ_web") }}
                    </h1>
                    <v-spacer></v-spacer>
                    <v-lazy v-if="isFaqsLoaded" transition="fade-transition">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            :label="searchLabel"
                            single-line
                            dense
                            hide-details
                            style="max-width: 200px"
                            class="mr-5 pr-5"
                        ></v-text-field>
                    </v-lazy>
                    <v-lazy transition="fade-transition">
                        <v-btn
                            color="success"
                            dark
                            small
                            @click.stop="
                                dialog.faqForm = true;
                                isFaqEdited = false;
                                selectedFaq = { ...defaultFaq };
                            "
                        >
                            {{ $t("general.add") }}
                        </v-btn>
                    </v-lazy>
                </v-row>
            </v-container>
        </header>

        <section class="py-7">
            <v-container>
                <v-sheet class="px-3 pt-3 pb-3" v-if="!isFaqsLoaded">
                    <v-skeleton-loader
                        class="mx-auto"
                        type="table"
                    ></v-skeleton-loader>
                </v-sheet>

                <v-card v-else>
                    <v-data-table
                        :headers="headers"
                        :items="faqs"
                        :search="search"
                    >
                        <template v-slot:item.action="{ item }">
                            <div style="display: flex">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            v-tr-class-on-hover="'blue'"
                                            size="20px"
                                            color="blue"
                                            class="mr-2"
                                            @click="editFaq(item)"
                                            v-on="on"
                                        >
                                            mdi-pencil-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t("general.edit") }}</span>
                                </v-tooltip>

                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            size="22px"
                                            color="red"
                                            v-tr-class-on-hover="'red'"
                                            class="mr-2"
                                            @click="confirmDelete(item)"
                                            v-on="on"
                                        >
                                            mdi-delete-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t("general.delete") }}</span>
                                </v-tooltip>
                            </div>
                        </template>

                        <template v-slot:item.question="{ item }">
                            <div class="text-truncate" style="max-width: 350px">
                                {{ item.question }}
                            </div>
                        </template>

                        <template
                            v-for="language in languages"
                            v-slot:[`item.question_${language.code}`]="{ item }"
                        >
                            <div
                                :key="`faq-question-${language.code}-${item.id}`"
                                class="text-truncate"
                                style="max-width: 300px"
                            >
                                {{ item[`question_${language.code}`] || "-" }}
                            </div>
                        </template>

                        <template v-slot:item.is_active="{ item }">
                            <v-switch
                                class="mt-0 pt-0 justify-content-center"
                                hide-details
                                color="green"
                                v-model="item.is_active"
                                @change="changeStatus(item)"
                            ></v-switch>
                        </template>
                    </v-data-table>
                </v-card>

                <FaqWebForm
                    :faq="selectedFaq"
                    :languages="languages"
                    :visible="dialog.faqForm"
                    :isEdited="isFaqEdited"
                    @addFaq="addFaq"
                    @updateFaq="updateFaq"
                    @close="dialog.faqForm = false"
                    @snackMessage="snackMessage"
                />

                <ConfirmationDialog
                    :name="selectedFaq.question"
                    :text="'faq_web.delete_confirmation'"
                    v-if="dialog.confirmation"
                    @confirm="deleteFaq"
                    @cancel="cancelDelete"
                />

                <SnackMessage ref="SnackMessage" />
            </v-container>
        </section>
    </div>
</template>

<script>
import FaqWebForm from "../components/FaqWebForm";
import ConfirmationDialog from "../components/ConfirmationDialog";
import SnackMessage from "../components/SnackMessage";

export default {
    data() {
        return {
            languages: [],
            faqs: [],
            isFaqsLoaded: false,
            search: "",
            dialog: {
                confirmation: false,
                faqForm: false,
            },
            selectedFaq: {
                id: null,
                question: "",
                answer: "",
                is_active: 0,
            },
            defaultFaq: {
                id: null,
                question: "",
                answer: "",
                is_active: 0,
            },
            isFaqEdited: false,
        };
    },

    components: {
        FaqWebForm,
        ConfirmationDialog,
        SnackMessage,
    },

    computed: {
        headers() {
            const baseHeaders = [
                {
                    text: this.$t("general.actions"),
                    value: "action",
                    sortable: false,
                    width: "100px",
                },
                {
                    text: this.$t("faq_web.question_cz"),
                    align: "left",
                    sortable: true,
                    value: "question",
                },
            ];

            const languageHeaders = this.languages.map((lang) => ({
                text:
                    this.$t("faq_web.question_lang") +
                    " " +
                    lang.name +
                    " (" +
                    lang.code +
                    ")",
                align: "left",
                sortable: false,
                value: `question_${lang.code}`,
            }));

            const activeHeader = {
                text: this.$t("general.active"),
                align: "center",
                sortable: false,
                value: "is_active",
                width: "100px",
            };

            return [...baseHeaders, ...languageHeaders, activeHeader];
        },
        searchLabel() {
            return this.$t("general.search");
        },
    },

    created() {
        this.getFaqs();
    },

    watch: {
        languages(newLanguages) {
            const base = {
                id: null,
                question: "",
                answer: "",
                is_active: 0,
            };

            newLanguages.forEach((lang) => {
                base[`question_${lang.code}`] = "";
                base[`answer_${lang.code}`] = "";
            });

            this.defaultFaq = base;

            if (!this.selectedFaq.id) {
                this.selectedFaq = { ...base };
            }
        },
    },

    methods: {
        snackMessage(msg, type) {
            this.$refs.SnackMessage.showMessage(msg, type);
        },

        async getFaqs() {
            try {
                const response = await axios.get("/faq-web", {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                });

                const { languages, faqs } = response.data;

                this.languages = (languages || []).filter(
                    (lang) => lang.code !== "cz"
                );

                this.faqs = faqs || [];
                this.isFaqsLoaded = true;
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },

        addFaq(faq) {
            this.faqs.push(faq);
        },

        editFaq(item) {
            this.selectedFaq = { ...item };
            this.dialog.faqForm = true;
            this.isFaqEdited = true;
        },

        updateFaq(faq) {
            const index = this.faqs.findIndex((item) => item.id == faq.id);
            if (index !== -1) {
                this.faqs.splice(index, 1, faq);
            }
        },

        confirmDelete(item) {
            this.selectedFaq = item;
            this.dialog.confirmation = true;
        },

        cancelDelete() {
            this.dialog.confirmation = false;
        },

        async deleteFaq() {
            this.dialog.confirmation = false;
            const index = this.faqs.indexOf(this.selectedFaq);

            try {
                await axios.delete(`/faq-web/${this.selectedFaq.id}`, {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                });

                if (index !== -1) {
                    this.faqs.splice(index, 1);
                }
                this.selectedFaq = { ...this.defaultFaq };
                this.snackMessage("faq_web.deleted_successfully", "success");
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },

        async changeStatus(item) {
            try {
                await axios.put(
                    `/faq-web/status/${item.id}`,
                    { is_active: item.is_active },
                    {
                        headers: {
                            Authorization:
                                "Bearer " +
                                this.$store.state.tokenData.user.access_token,
                        },
                    }
                );
                this.snackMessage("general.item_saved", "success");
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },
    },
};
</script>
